import React, { useState, useRef, FC } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from 'react-image-crop';

import { CanvasPreview } from './CanvasPrewiew';
import { useDebounceEffect } from './useDebounceEffect';
import { Button, Stack } from '@mui/material';
import { EmptyFilesIcon } from '~/shared/icons/EmptyFilesIcon';
import { ICropImageProps } from '~/shared/types/types';
import { validateImageFile } from '~/shared/utils/validateImage';
import { useSnackbar } from 'notistack';

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export const CropImage: FC<ICropImageProps> = ({
  onChange,
  onNameChange,
  title,
  handleClose,
  aspect: aspectProp,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('image');
  const [selectedFileType, setSelectedFileType] = useState('image/png');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(aspectProp || 1 / 1);

  async function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const sourceFile = e.target.files[0];
      const error = await validateImageFile(sourceFile);

      if (error) {
        enqueueSnackbar(error, {
          variant: 'error',
          autoHideDuration: 5000,
          style: { maxWidth: '700px' },
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        e.target.value = '';
        return;
      }

      onNameChange(sourceFile?.name);
      setSelectedFileName(sourceFile?.name || 'image');
      setSelectedFileType(sourceFile?.type || 'image/png');
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || ''),
      );
      reader.readAsDataURL(sourceFile);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  async function onDownloadCropClick() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    // This will size relative to the uploaded image
    // size. If you want to size according to what they
    // are looking at on screen, remove scaleX + scaleY
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const outputWidth = Math.max(1, Math.round(completedCrop.width * scaleX));
    const outputHeight = Math.max(1, Math.round(completedCrop.height * scaleY));
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;

    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      outputCanvas.width,
      outputCanvas.height,
    );
    const outputType = selectedFileType === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await new Promise<Blob | null>(resolve => {
      outputCanvas.toBlob(
        resolve,
        outputType,
        outputType === 'image/jpeg' ? 0.95 : undefined,
      );
    });
    if (!blob) {
      throw new Error('Не удалось сформировать изображение');
    }

    const nextExtension = outputType === 'image/jpeg' ? 'jpg' : 'png';
    const nextName = selectedFileName.replace(/\.[^.]+$/, '') || 'image';
    const file = new File([blob], `${nextName}.${nextExtension}`, {
      type: outputType,
      lastModified: Date.now(),
    });

    onChange(file);
    handleClose(false);
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        CanvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        );
      }
    },
    100,
    [completedCrop, scale, rotate],
  );

  const ref = useRef(null);

  return (
    <div className="App">
      <div className="Crop-Controls">
        {!imgSrc && (
          <>
            <Stack width="400px" margin="0 auto">
              <EmptyFilesIcon />
            </Stack>
            <Stack
              display="flex"
              justifyContent="center"
              marginTop="24px"
              flexDirection="row"
              gap="24px">
              <Stack flex={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleClose(!open)}>
                  Отмена
                </Button>
              </Stack>
              <Stack flex={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => ref.current.click()}>
                  Обзор
                </Button>
              </Stack>
            </Stack>
          </>
        )}

        <input
          type="file"
          accept="image/png, image/jpeg"
          onChange={onSelectFile}
          ref={ref}
          style={{ display: 'none' }}
        />
      </div>
      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={c => setCompletedCrop(c)}
          aspect={aspect}
          minHeight={100}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      )}
      {!!completedCrop && (
        <>
          <div style={{ display: 'none' }}>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingTop: '54px',
              gap: '12px',
            }}>
            <Button
              sx={{ flex: 1 }}
              component="label"
              color="secondary"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              onClick={() => handleClose(false)}>
              Отмена
            </Button>
            <Button
              sx={{ flex: 1 }}
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              onClick={onDownloadCropClick}
              startIcon={<CloudUploadIcon />}>
              {title}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
